/**
 * Dashboard.js - User dashboard showing all leagues
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import * as api from '../services/api';
import { formatDate } from '../utils/dateFormat';
import FairnessReport from './FairnessReport';
import { getPlanDisplayName } from '../utils/planNames';
import './Dashboard.css';

function Dashboard({ onCreateNew, onViewLeague, onPlayoffs, onGameManagement, onRegistration, onRoster, onWebsite, onPickleballRatings }) {
  const { user } = useAuth();
  const { colors } = useTheme();
  const [leagues, setLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [toast, setToast] = useState(null);
  const [deleteMode, setDeleteMode] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null); // { leagueId, leagueName }
  const [notifyingLeague, setNotifyingLeague] = useState(null); // leagueId currently sending
  const [remindingLeague, setRemindingLeague] = useState(null); // leagueId currently sending reminders
  const [generatingLinks, setGeneratingLinks] = useState(null); // leagueId currently generating
  const [captainLinks, setCaptainLinks] = useState(null); // { leagueId, leagueName, tokens }
  const [pendingScoresModal, setPendingScoresModal] = useState(null); // { leagueId, leagueName, scores }
  const [reviewingScore, setReviewingScore] = useState(null); // pendingScoreId currently being reviewed
  const [archiveModal, setArchiveModal] = useState(null); // { leagueId, leagueName }
  const [archiveSeasonName, setArchiveSeasonName] = useState('');
  const [archivingLeague, setArchivingLeague] = useState(null); // leagueId currently archiving
  const [seasonsModal, setSeasonsModal] = useState(null); // { leagueId, leagueName, seasons }
  const [loadingSeasons, setLoadingSeasons] = useState(null); // leagueId currently loading
  const [seasonViewer, setSeasonViewer] = useState(null); // { leagueId, season } - full season data
  const [loadingSeasonDetail, setLoadingSeasonDetail] = useState(false);
  const [embedModal, setEmbedModal] = useState(null); // { leagueName, slug }
  const [customizeModal, setCustomizeModal] = useState(null); // { leagueId, leagueName, logoUrl, description, primaryColor }
  const [customizeForm, setCustomizeForm] = useState({ logoUrl: '', description: '', primaryColor: '', slug: '', city: '', state: '' });
  const [savingCustomize, setSavingCustomize] = useState(false);
  const [slugValidation, setSlugValidation] = useState({ status: null, message: '' }); // status: null | 'valid' | 'error' | 'checking'
  const [logoUrlValidation, setLogoUrlValidation] = useState({ status: null, message: '' }); // status: null | 'valid' | 'error'
  const slugCheckTimer = useRef(null);
  const [confirmComplete, setConfirmComplete] = useState(null); // { leagueId, leagueName }
  const [searchQuery, setSearchQuery] = useState('');
  const [sortOption, setSortOption] = useState(() => {
    return localStorage.getItem('leagueMaker_sortPreference') || 'newest';
  });
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('leagueMaker_dashboardTab') || 'active';
  });
  const [copiedLinkId, setCopiedLinkId] = useState(null); // leagueId + type key that was just copied

  useEffect(() => {
    loadLeagues();
  }, []);


  const showToast = (message, type = 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const loadLeagues = async () => {
    try {
      setLoading(true);
      const response = await api.getLeagues();
      setLeagues(response.leagues);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStandings = async (leagueId, currentStatus) => {
    try {
      await api.updateLeague(leagueId, { showStandings: !currentStatus });
      loadLeagues();
    } catch (err) {
      showToast('Failed to update standings visibility: ' + err.message);
    }
  };

  const handleToggleNotifyOnScores = async (leagueId, currentStatus) => {
    try {
      await api.updateLeague(leagueId, { notifyOnScores: !currentStatus });
      loadLeagues();
    } catch (err) {
      showToast('Failed to update score notifications: ' + err.message);
    }
  };

  const handleTogglePublic = async (leagueId, currentStatus) => {
    try {
      await api.updateLeague(leagueId, { isPublic: !currentStatus });
      // Reload leagues to show updated status
      loadLeagues();
    } catch (err) {
      showToast('Failed to update league: ' + err.message);
    }
  };

  const handleMarkComplete = async () => {
    if (!confirmComplete) return;
    try {
      await api.updateLeague(confirmComplete.leagueId, { status: 'completed' });
      setConfirmComplete(null);
      showToast('League marked as completed', 'success');
      loadLeagues();
    } catch (err) {
      showToast('Failed to mark league as completed: ' + err.message);
    }
  };

  const handleReactivateLeague = async (leagueId) => {
    try {
      await api.updateLeague(leagueId, { status: 'published' });
      showToast('League reactivated!', 'success');
      loadLeagues();
    } catch (err) {
      showToast('Failed to reactivate league: ' + err.message);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    localStorage.setItem('leagueMaker_dashboardTab', tab);
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.deleteLeague(confirmDelete.leagueId);
      setConfirmDelete(null);
      setDeleteMode(false);
      loadLeagues();
    } catch (err) {
      setConfirmDelete(null);
      showToast('Failed to delete league: ' + err.message);
    }
  };

  const handleDuplicateLeague = async (league) => {
    try {
      await api.duplicateLeague(league.id);
      showToast('League duplicated! Edit dates and time slots to generate a new schedule.', 'success');
      loadLeagues();
    } catch (err) {
      showToast(err.message || 'Failed to duplicate league');
    }
  };

  const handleOpenCustomize = (league) => {
    setCustomizeForm({
      logoUrl: league.logoUrl || '',
      description: league.description || '',
      primaryColor: league.primaryColor || '',
      slug: league.slug || '',
      city: league.city || '',
      state: league.state || ''
    });
    setSlugValidation({ status: null, message: '' });
    setLogoUrlValidation({ status: null, message: '' });
    setCustomizeModal({ leagueId: league.id, leagueName: league.name, originalSlug: league.slug });
  };

  const validateSlugFormat = useCallback((slug) => {
    if (!slug) return { status: null, message: '' };
    if (slug.length < 3) return { status: 'error', message: 'Must be at least 3 characters' };
    if (slug.length > 50) return { status: 'error', message: 'Must be 50 characters or less' };
    if (!/^[a-z0-9-]+$/.test(slug)) return { status: 'error', message: 'Only lowercase letters, numbers, and hyphens' };
    if (/^-/.test(slug) || /-$/.test(slug)) return { status: 'error', message: 'Cannot start or end with a hyphen' };
    if (/--/.test(slug)) return { status: 'error', message: 'Cannot contain consecutive hyphens' };
    return { status: 'valid', message: '' };
  }, []);

  const validateLogoUrl = useCallback((url) => {
    if (!url || url.trim() === '') return { status: null, message: '' };
    try {
      const parsed = new URL(url);
      if (!['http:', 'https:'].includes(parsed.protocol)) {
        return { status: 'error', message: 'Must use http:// or https://' };
      }
      return { status: 'valid', message: '' };
    } catch {
      return { status: 'error', message: 'Enter a valid URL (e.g. https://example.com/logo.png)' };
    }
  }, []);

  const handleSlugChange = useCallback((value) => {
    const slug = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setCustomizeForm(f => ({ ...f, slug }));

    const formatResult = validateSlugFormat(slug);
    if (formatResult.status === 'error' || formatResult.status === null) {
      setSlugValidation(formatResult);
      return;
    }

    // If slug unchanged from original, mark as valid immediately
    if (customizeModal && slug === customizeModal.originalSlug) {
      setSlugValidation({ status: 'valid', message: '' });
      return;
    }

    // Debounce the uniqueness check
    setSlugValidation({ status: 'checking', message: 'Checking availability...' });
    if (slugCheckTimer.current) clearTimeout(slugCheckTimer.current);
    slugCheckTimer.current = setTimeout(async () => {
      try {
        const result = await api.checkSlugAvailability(slug, customizeModal?.leagueId);
        if (result.available) {
          setSlugValidation({ status: 'valid', message: 'Available' });
        } else {
          setSlugValidation({ status: 'error', message: 'This slug is already taken' });
        }
      } catch {
        setSlugValidation({ status: 'error', message: 'Could not check availability' });
      }
    }, 400);
  }, [customizeModal, validateSlugFormat]);

  const handleLogoUrlChange = useCallback((value) => {
    setCustomizeForm(f => ({ ...f, logoUrl: value }));
    setLogoUrlValidation(validateLogoUrl(value));
  }, [validateLogoUrl]);

  const hasCustomizeErrors = slugValidation.status === 'error' || slugValidation.status === 'checking' || logoUrlValidation.status === 'error';

  const handleSaveCustomize = async () => {
    if (!customizeModal || hasCustomizeErrors) return;
    try {
      setSavingCustomize(true);
      const updates = {
        logoUrl: customizeForm.logoUrl || null,
        description: customizeForm.description || null,
        primaryColor: customizeForm.primaryColor || null,
        city: customizeForm.city || null,
        state: customizeForm.state || null
      };
      // Only send slug if it changed from the original
      if (customizeForm.slug && customizeForm.slug !== customizeModal.originalSlug) {
        updates.slug = customizeForm.slug;
      }
      await api.updateLeague(customizeModal.leagueId, updates);
      showToast('Customization saved!', 'success');
      setCustomizeModal(null);
      loadLeagues();
    } catch (err) {
      showToast(err.message || 'Failed to save customization');
    } finally {
      setSavingCustomize(false);
    }
  };

  const handleDownloadPDF = async (league) => {
    try {
      const response = await api.getLeague(league.id);
      const originalInput = response.league.scheduleData?.originalInput;

      if (!originalInput) {
        showToast('Cannot generate PDF - missing schedule data');
        return;
      }

      // Build venue and referee maps from game records for PDF
      const leagueData = response.league;
      const teamMap = {};
      (leagueData.teams || []).forEach(t => { teamMap[t.id] = t.name; });
      const venueMap = {};
      const refereeMap = {};
      const gameStatusMap = {};
      const scoreMap = {};
      (leagueData.games || []).forEach(game => {
        const key = `${game.week}|${game.time}|${teamMap[game.team1Id]}|${teamMap[game.team2Id]}`;
        if (game.venue?.name) {
          venueMap[key] = game.venue.name;
        }
        if (game.referee?.name) {
          refereeMap[key] = game.referee.name;
        }
        const gs = game.status || 'scheduled';
        if (gs !== 'scheduled' && gs !== 'completed') {
          gameStatusMap[key] = {
            status: gs,
            forfeitTeam: game.forfeitTeam?.name || null,
            rescheduledDate: game.rescheduledDate || null
          };
        }
        if (game.played && game.team1Score != null && game.team2Score != null) {
          scoreMap[key] = {
            team1Score: game.team1Score,
            team2Score: game.team2Score,
            team1: teamMap[game.team1Id],
            team2: teamMap[game.team2Id]
          };
        }
      });

      const cleanData = {
        leagueName: league.name,
        seasonYear: league.seasonYear,
        sport: league.sport || undefined,
        roundRobinTeams: originalInput.roundRobinTeams,
        roundRobinGroup2Teams: originalInput.roundRobinGroup2Teams || originalInput.dedicatedPairTeams || [],
        gameDates: originalInput.gameDates,
        timeSlots: originalInput.timeSlots,
        status: league.status,
        venueMap: Object.keys(venueMap).length > 0 ? venueMap : undefined,
        refereeMap: Object.keys(refereeMap).length > 0 ? refereeMap : undefined,
        gameStatusMap: Object.keys(gameStatusMap).length > 0 ? gameStatusMap : undefined,
        scoreMap: Object.keys(scoreMap).length > 0 ? scoreMap : undefined
      };

      const blob = await api.generatePDF(cleanData);
      const filename = `${league.name.replace(/[^a-z0-9]/gi, '_')}_schedule.pdf`;
      api.downloadFile(blob, filename);
    } catch (err) {
      showToast('Failed to generate PDF: ' + err.message);
    }
  };

  const handleNotifyCaptains = async (leagueId) => {
    try {
      setNotifyingLeague(leagueId);
      const result = await api.notifyCaptains(leagueId, 'schedule_published');
      showToast(`Emails sent to ${result.sent} captain${result.sent !== 1 ? 's' : ''}${result.skipped ? ` (${result.skipped} skipped)` : ''}`, 'success');
    } catch (err) {
      showToast('Failed to send notifications: ' + err.message);
    } finally {
      setNotifyingLeague(null);
    }
  };

  const handleSendReminders = async (leagueId) => {
    try {
      setRemindingLeague(leagueId);
      const result = await api.notifyCaptains(leagueId, 'send_reminders');
      if (result.gamesFound === 0) {
        showToast('No upcoming games in the next 48 hours', 'success');
      } else {
        showToast(`Reminders sent to ${result.sent} captain${result.sent !== 1 ? 's' : ''}${result.skipped ? ` (${result.skipped} skipped)` : ''} for ${result.gamesFound} game${result.gamesFound !== 1 ? 's' : ''}`, 'success');
      }
    } catch (err) {
      showToast('Failed to send reminders: ' + err.message);
    } finally {
      setRemindingLeague(null);
    }
  };

  const handleToggleCaptainScoreEntry = async (leagueId, currentStatus) => {
    try {
      await api.updateLeague(leagueId, { captainScoreEntry: !currentStatus });
      loadLeagues();
    } catch (err) {
      showToast('Failed to update captain score entry: ' + err.message);
    }
  };

  const handleToggleRefereeScoreEntry = async (leagueId, currentStatus) => {
    try {
      await api.updateLeague(leagueId, { refereeScoreEntry: !currentStatus });
      loadLeagues();
    } catch (err) {
      showToast('Failed to update referee score entry: ' + err.message);
    }
  };

  const handleOpenPendingScores = async (leagueId, leagueName) => {
    try {
      const result = await api.getPendingScores(leagueId);
      setPendingScoresModal({
        leagueId,
        leagueName,
        scores: result.pendingScores
      });
    } catch (err) {
      showToast('Failed to load pending scores: ' + err.message);
    }
  };

  const handleReviewScore = async (pendingId, status) => {
    if (!pendingScoresModal) return;
    setReviewingScore(pendingId);
    try {
      await api.reviewPendingScore(pendingScoresModal.leagueId, pendingId, status);
      // Remove from modal list
      setPendingScoresModal(prev => ({
        ...prev,
        scores: prev.scores.filter(s => s.id !== pendingId)
      }));
      showToast(`Score ${status}`, 'success');
      loadLeagues(); // Refresh counts
    } catch (err) {
      showToast(`Failed to ${status} score: ` + err.message);
    } finally {
      setReviewingScore(null);
    }
  };

  const handleGenerateCaptainLinks = async (leagueId, leagueName) => {
    try {
      setGeneratingLinks(leagueId);
      const result = await api.generateCaptainLinks(leagueId);
      setCaptainLinks({
        leagueId,
        leagueName,
        tokens: result.tokens.map(t => ({
          teamName: t.teamName,
          email: t.email,
          url: `${window.location.origin}/captain/${t.token}`
        }))
      });
    } catch (err) {
      showToast('Failed to generate captain links: ' + err.message);
    } finally {
      setGeneratingLinks(null);
    }
  };

  const copyAllCaptainLinks = () => {
    if (!captainLinks) return;
    const text = captainLinks.tokens
      .map(t => `${t.teamName} (${t.email})\n${t.url}`)
      .join('\n\n');
    navigator.clipboard.writeText(text);
    showToast('All captain links copied!', 'success');
  };

  const handleArchiveSeason = async () => {
    if (!archiveModal || !archiveSeasonName.trim()) {
      showToast('Please enter a season name');
      return;
    }
    try {
      setArchivingLeague(archiveModal.leagueId);
      await api.archiveSeason(archiveModal.leagueId, archiveSeasonName.trim());
      showToast(`Season "${archiveSeasonName.trim()}" archived successfully!`, 'success');
      setArchiveModal(null);
      setArchiveSeasonName('');
      loadLeagues();
    } catch (err) {
      showToast('Failed to archive season: ' + err.message);
    } finally {
      setArchivingLeague(null);
    }
  };

  const handleOpenSeasons = async (leagueId, leagueName) => {
    try {
      setLoadingSeasons(leagueId);
      const result = await api.getSeasons(leagueId);
      setSeasonsModal({ leagueId, leagueName, seasons: result.seasons });
    } catch (err) {
      showToast('Failed to load seasons: ' + err.message);
    } finally {
      setLoadingSeasons(null);
    }
  };

  const handleViewSeason = async (leagueId, seasonId) => {
    try {
      setLoadingSeasonDetail(true);
      const result = await api.getSeason(leagueId, seasonId);
      setSeasonViewer({ leagueId, season: result.season });
    } catch (err) {
      showToast('Failed to load season: ' + err.message);
    } finally {
      setLoadingSeasonDetail(false);
    }
  };

  const getPublicUrl = (slug) => {
    return `${window.location.origin}/public/${slug}`;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showToast('URL copied to clipboard!', 'success');
  };

  const copyLinkWithFeedback = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopiedLinkId(key);
    setTimeout(() => setCopiedLinkId(null), 2000);
  };


  // Derive a 6-stage lifecycle status for a league card
  function getLifecycleStatus(league) {
    if (league.status === 'completed') return 'season-complete';
    if (league.status === 'published') return 'season-active';
    // status === 'draft' from here
    if (league.registrationStatus === 'closed') return 'reg-closed';
    if (league.registrationStatus === 'open') return 'reg-open';
    return 'setup';
  }

  const LIFECYCLE_LABELS = {
    'setup': 'Setup',
    'reg-open': 'Registration Open',
    'reg-closed': 'Registration Closed',
    'season-active': 'Schedule Generated',
    'season-complete': 'Season Complete'
  };

  const LIFECYCLE_COLORS = {
    'setup': { bg: '#f0f4ff', color: '#4a6cf7', border: '#c7d3fd' },
    'reg-open': { bg: '#e6f7ee', color: '#16a34a', border: '#a7f3d0' },
    'reg-closed': { bg: '#fff7ed', color: '#c2410c', border: '#fed7aa' },
    'season-active': { bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
    'season-complete': { bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' }
  };

  const userPlan = user?.plan || 'free';
  const leagueLimit = userPlan === 'pro' ? Infinity : 1;
  const activeLeagues = leagues.filter(l => l.status !== 'completed');
  const completedLeagues = leagues.filter(l => l.status === 'completed');
  const atLeagueLimit = activeLeagues.length >= leagueLimit;

  const handleSortChange = (newSort) => {
    setSortOption(newSort);
    localStorage.setItem('leagueMaker_sortPreference', newSort);
  };

  const filteredAndSortedLeagues = React.useMemo(() => {
    let result = activeTab === 'active' ? activeLeagues : completedLeagues;
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(l => l.name.toLowerCase().includes(q));
    }
    const sorted = [...result];
    switch (sortOption) {
      case 'name-asc':
        sorted.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 'name-desc':
        sorted.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 'newest':
        sorted.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        break;
      case 'oldest':
        sorted.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));
        break;
      case 'status':
        const statusOrder = { draft: 0, published: 1, completed: 2 };
        sorted.sort((a, b) => (statusOrder[a.status] ?? 3) - (statusOrder[b.status] ?? 3));
        break;
      default:
        break;
    }
    return sorted;
  }, [searchQuery, sortOption, activeTab, activeLeagues, completedLeagues]);

  const planBadgeStyle = {
    display: 'inline-block',
    padding: '2px 10px',
    borderRadius: '12px',
    fontSize: '0.75rem',
    fontWeight: '600',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginLeft: '8px',
    verticalAlign: 'middle',
    color: '#fff',
    background: userPlan === 'pro' ? '#764ba2' : userPlan === 'starter' ? colors.brand : colors.textSecondary
  };

  if (loading) {
    return (
      <div className="dashboard">
        <div className="dashboard-loading">Loading your leagues...</div>
      </div>
    );
  }

  return (
    <div className="dashboard">
      <div className="dashboard-header">
        <div>
          <h1>My Leagues</h1>
          <p className="welcome-text">
            Welcome back, {user?.name}!
            <span style={planBadgeStyle}>{getPlanDisplayName(userPlan)}</span>
          </p>
        </div>
        <div className="header-actions">
          <button
            onClick={onCreateNew}
            className="create-button"
            disabled={atLeagueLimit}
            style={atLeagueLimit ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
            title={atLeagueLimit ? 'Upgrade to Commissioner for unlimited leagues' : ''}
          >
            + Create New League
          </button>
          {atLeagueLimit && (
            <span style={{ fontSize: '0.8rem', color: colors.brand, marginLeft: '8px' }}>
              Upgrade to Commissioner for unlimited leagues
            </span>
          )}
          {leagues.length > 0 && (
            <button
              onClick={() => setDeleteMode(!deleteMode)}
              className={`manage-button${deleteMode ? ' active' : ''}`}
            >
              {deleteMode ? 'Done' : 'Manage'}
            </button>
          )}
        </div>
      </div>

      {toast && (
        <div className={`toast toast-${toast.type}`}>
          {toast.message}
          <button className="toast-close" onClick={() => setToast(null)}>x</button>
        </div>
      )}

      {error && <div className="error-message">{error}</div>}

      {leagues.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h2>No leagues yet</h2>
          <p>Create your first league to get started!</p>
          <button
            onClick={onCreateNew}
            className="create-button"
            disabled={atLeagueLimit}
            style={atLeagueLimit ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            Create League
          </button>
        </div>
      ) : (
        <>
          <div className="dash-tab-bar">
            <button
              className={`dash-tab${activeTab === 'active' ? ' active' : ''}`}
              onClick={() => handleTabChange('active')}
            >
              Active ({activeLeagues.length})
            </button>
            <button
              className={`dash-tab${activeTab === 'completed' ? ' active' : ''}`}
              onClick={() => handleTabChange('completed')}
            >
              Completed ({completedLeagues.length})
            </button>
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '12px',
            marginBottom: '16px', flexWrap: 'wrap'
          }}>
            <div style={{ position: 'relative', flex: '1 1 220px', minWidth: '180px' }}>
              <input
                type="text"
                placeholder="Search leagues..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%', padding: '8px 32px 8px 12px',
                  borderRadius: '8px', border: `1px solid ${colors.inputBorder}`,
                  fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box',
                  background: colors.inputBg, color: colors.text
                }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{
                    position: 'absolute', right: '8px', top: '50%',
                    transform: 'translateY(-50%)', background: 'none',
                    border: 'none', cursor: 'pointer', fontSize: '1rem',
                    color: colors.textSecondary, padding: '0 4px', lineHeight: 1
                  }}
                  title="Clear search"
                >
                  ×
                </button>
              )}
            </div>
            <select
              value={sortOption}
              onChange={(e) => handleSortChange(e.target.value)}
              style={{
                padding: '8px 12px', borderRadius: '8px',
                border: `1px solid ${colors.inputBorder}`, fontSize: '0.9rem',
                background: colors.inputBg, color: colors.text, cursor: 'pointer', outline: 'none'
              }}
            >
              <option value="newest">Newest first</option>
              <option value="oldest">Oldest first</option>
              <option value="name-asc">Name (A-Z)</option>
              <option value="name-desc">Name (Z-A)</option>
              <option value="status">Status</option>
            </select>
          </div>

          {filteredAndSortedLeagues.length === 0 ? (
            <div style={{
              textAlign: 'center', padding: '40px 20px', color: colors.textSecondary
            }}>
              <p style={{ fontSize: '1.1rem', margin: 0 }}>
                {searchQuery
                  ? `No leagues matching "${searchQuery}"`
                  : activeTab === 'completed'
                    ? 'No completed leagues yet'
                    : 'No active leagues'
                }
              </p>
            </div>
          ) : (
        <div className="leagues-grid">
          {filteredAndSortedLeagues.map((league) => {
            const isCompleted = league.status === 'completed';
            const lifecycleStatus = getLifecycleStatus(league);
            const lifecycleColors = LIFECYCLE_COLORS[lifecycleStatus];
            const regLink = `${window.location.origin}/register/${league.id}`;
            const websiteLink = league.isPublic && league.slug ? getPublicUrl(league.slug) : null;
            return (
            <div key={league.id} className={`league-card${isCompleted ? ' league-card-completed' : ''}`}>
              <div className="league-card-header">
                <h3>{league.name}</h3>
                <span style={{
                  display: 'inline-block',
                  padding: '2px 10px',
                  borderRadius: '12px',
                  fontSize: '0.72rem',
                  fontWeight: '600',
                  letterSpacing: '0.3px',
                  background: lifecycleColors.bg,
                  color: lifecycleColors.color,
                  border: `1px solid ${lifecycleColors.border}`
                }}>
                  {LIFECYCLE_LABELS[lifecycleStatus]}
                </span>
              </div>

              <div className="league-card-body">
                <p className="league-info">
                  <strong>Season:</strong> {league.seasonYear}
                </p>
                {(league.city || league.state) && (
                  <p className="league-info" style={{ color: '#718096' }}>
                    {[league.city, league.state].filter(Boolean).join(', ')}
                  </p>
                )}
                {league.sport && (
                  <p className="league-info">
                    <strong>Sport:</strong> {league.sport}{league.bestOf && ` — Best of ${league.bestOf}`}
                  </p>
                )}
                <p className="league-info">
                  <strong>Teams:</strong> {league.teamCount}
                </p>
                <p className="league-info">
                  <strong>Created:</strong> {formatDate(league.createdAt)}
                </p>

                {/* Registration closed → ready to generate schedule prompt */}
                {lifecycleStatus === 'reg-closed' && (
                  <div style={{
                    margin: '12px 0',
                    padding: '12px 14px',
                    borderRadius: '8px',
                    background: '#fff7ed',
                    border: '1px solid #fed7aa',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px'
                  }}>
                    <p style={{ margin: 0, fontSize: '0.85rem', fontWeight: 600, color: '#c2410c' }}>
                      Registration closed with {league.registrationTeamCount > 0
                        ? `${league.registrationTeamCount} team${league.registrationTeamCount !== 1 ? 's' : ''}`
                        : `${league.registrationTotalCount} player${league.registrationTotalCount !== 1 ? 's' : ''}`}
                    </p>
                    <p style={{ margin: 0, fontSize: '0.82rem', color: '#92400e' }}>
                      Ready to generate your schedule?
                    </p>
                    <button
                      onClick={() => onViewLeague(league.id)}
                      style={{
                        alignSelf: 'flex-start',
                        padding: '6px 14px',
                        background: 'linear-gradient(135deg, #f97316 0%, #ea580c 100%)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '0.82rem',
                        fontWeight: 600,
                        cursor: 'pointer'
                      }}
                    >
                      Generate Schedule →
                    </button>
                  </div>
                )}

                {/* Registration open → share links section */}
                {lifecycleStatus === 'reg-open' && (
                  <div style={{
                    margin: '12px 0',
                    padding: '12px 14px',
                    borderRadius: '8px',
                    background: '#e6f7ee',
                    border: '1px solid #a7f3d0'
                  }}>
                    <p style={{ margin: '0 0 8px', fontSize: '0.82rem', fontWeight: 600, color: '#065f46' }}>
                      Registration is open — share your link
                    </p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '6px' }}>
                      <span style={{ fontSize: '0.75rem', color: '#065f46', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {regLink}
                      </span>
                      <button
                        onClick={() => copyLinkWithFeedback(regLink, `${league.id}-reg`)}
                        style={{
                          flexShrink: 0,
                          padding: '3px 10px',
                          background: copiedLinkId === `${league.id}-reg` ? '#16a34a' : '#fff',
                          color: copiedLinkId === `${league.id}-reg` ? '#fff' : '#065f46',
                          border: '1px solid #16a34a',
                          borderRadius: '5px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                      >
                        {copiedLinkId === `${league.id}-reg` ? 'Copied!' : 'Copy'}
                      </button>
                    </div>
                    {websiteLink && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <span style={{ fontSize: '0.75rem', color: '#065f46', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {websiteLink}
                        </span>
                        <button
                          onClick={() => copyLinkWithFeedback(websiteLink, `${league.id}-web`)}
                          style={{
                            flexShrink: 0,
                            padding: '3px 10px',
                            background: copiedLinkId === `${league.id}-web` ? '#16a34a' : '#fff',
                            color: copiedLinkId === `${league.id}-web` ? '#fff' : '#065f46',
                            border: '1px solid #16a34a',
                            borderRadius: '5px',
                            fontSize: '0.75rem',
                            fontWeight: 600,
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                        >
                          {copiedLinkId === `${league.id}-web` ? 'Copied!' : 'Copy Site'}
                        </button>
                      </div>
                    )}
                  </div>
                )}

                <div className="dash-toggles-section" style={isCompleted ? { opacity: 0.5, pointerEvents: 'none' } : {}}>
                  <div className={`dash-toggle-row${league.status === 'draft' ? ' disabled' : ''}`}>
                    <label className="dash-toggle-switch">
                      <input
                        type="checkbox"
                        checked={league.isPublic}
                        onChange={() => handleTogglePublic(league.id, league.isPublic)}
                        disabled={league.status === 'draft' || isCompleted}
                      />
                      <span className="dash-toggle-slider"></span>
                    </label>
                    <span className="dash-toggle-label">Public Page</span>
                    {league.status === 'draft' && <span className="draft-hint">Publish to enable</span>}
                    {league.isPublic && userPlan !== 'free' && (
                      <button
                        onClick={() => handleOpenCustomize(league)}
                        className="action-button secondary"
                        style={{ padding: '0.3rem 0.6rem', fontSize: '0.8rem', marginLeft: 'auto' }}
                      >
                        Customize
                      </button>
                    )}
                  </div>

                  <div className={`dash-toggle-row${!league.isPublic ? ' disabled' : ''}`}>
                    <label className="dash-toggle-switch">
                      <input
                        type="checkbox"
                        checked={league.isPublic && league.showStandings !== false}
                        onChange={() => handleToggleStandings(league.id, league.showStandings !== false)}
                        disabled={!league.isPublic}
                      />
                      <span className="dash-toggle-slider"></span>
                    </label>
                    <span className="dash-toggle-label">Show Standings</span>
                  </div>

                  <div className={`dash-toggle-row${userPlan === 'free' ? ' disabled' : ''}`}>
                    <label className="dash-toggle-switch">
                      <input
                        type="checkbox"
                        checked={!!league.notifyOnScores}
                        onChange={() => handleToggleNotifyOnScores(league.id, !!league.notifyOnScores)}
                        disabled={userPlan === 'free'}
                      />
                      <span className="dash-toggle-slider"></span>
                    </label>
                    <span className="dash-toggle-label">Email scores to captains</span>
                    {userPlan === 'free' && <span className="draft-hint">Requires League</span>}
                  </div>

                  <div className={`dash-toggle-row${userPlan !== 'pro' ? ' disabled' : ''}`}>
                    <label className="dash-toggle-switch">
                      <input
                        type="checkbox"
                        checked={!!league.captainScoreEntry}
                        onChange={() => handleToggleCaptainScoreEntry(league.id, !!league.captainScoreEntry)}
                        disabled={userPlan !== 'pro'}
                      />
                      <span className="dash-toggle-slider"></span>
                    </label>
                    <span className="dash-toggle-label">Captain score submission</span>
                    {userPlan !== 'pro' && <span className="draft-hint">Requires Commissioner</span>}
                  </div>

                  <div className={`dash-toggle-row${userPlan !== 'pro' ? ' disabled' : ''}`}>
                    <label className="dash-toggle-switch">
                      <input
                        type="checkbox"
                        checked={!!league.refereeScoreEntry}
                        onChange={() => handleToggleRefereeScoreEntry(league.id, !!league.refereeScoreEntry)}
                        disabled={userPlan !== 'pro'}
                      />
                      <span className="dash-toggle-slider"></span>
                    </label>
                    <span className="dash-toggle-label">Referee score submission</span>
                    {userPlan !== 'pro' && <span className="draft-hint">Requires Commissioner</span>}
                  </div>
                </div>

                <div className={`public-url${!league.isPublic ? ' disabled' : ''}`}>
                  <p className="url-label">Public URL:</p>
                  <div className="url-box">
                    <code>{league.isPublic ? getPublicUrl(league.slug) : 'Enable Public Page to generate URL'}</code>
                    <button
                      onClick={() => league.isPublic && copyToClipboard(getPublicUrl(league.slug))}
                      className="copy-button"
                      title="Copy URL"
                    >
                      📋
                    </button>
                    {league.isPublic && (
                      <a
                        href={getPublicUrl(league.slug)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="open-link-button"
                        title="Open public page in new tab"
                      >
                        Open ↗
                      </a>
                    )}
                  </div>
                </div>

                {league.status === 'draft' && (
                  <FairnessReport leagueId={league.id} />
                )}
              </div>

              <div className="league-card-actions">
                <button
                  onClick={() => onViewLeague(league.id)}
                  className="action-button primary"
                >
                  View
                </button>
                {!isCompleted && (
                  <button
                    onClick={() => onGameManagement(league.id)}
                    className="action-button secondary"
                  >
                    Manage Games
                  </button>
                )}
                {!isCompleted && (
                  <button
                    onClick={() => userPlan !== 'free' && onRegistration(league.id)}
                    className="action-button secondary"
                    disabled={userPlan === 'free'}
                    style={userPlan === 'free' ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                    title={userPlan === 'free' ? 'Upgrade to League to accept online registrations' : ''}
                  >
                    Registration{userPlan === 'free' ? ' 🔒' : ''}
                  </button>
                )}
                {!isCompleted && (
                  <button
                    onClick={() => onRoster(league.id)}
                    className="action-button secondary"
                  >
                    Roster
                  </button>
                )}
                {!isCompleted && (
                  <button
                    onClick={() => userPlan === 'pro' && onWebsite(league.id, league.name)}
                    className="action-button secondary"
                    disabled={userPlan !== 'pro'}
                    style={userPlan !== 'pro' ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                    title={userPlan !== 'pro' ? 'Upgrade to Commissioner to create a branded league website' : ''}
                  >
                    Website{userPlan !== 'pro' ? ' 🔒' : ''}
                  </button>
                )}
                {league.sport === 'pickleball' && (
                  <button
                    onClick={() => onPickleballRatings && onPickleballRatings(league.id, league.name)}
                    className="action-button secondary"
                  >
                    Ratings
                  </button>
                )}
                {!isCompleted && league.playoffSettings?.enabled && (
                  <button
                    onClick={() => userPlan === 'pro' && onPlayoffs(league.id)}
                    className="action-button secondary"
                    disabled={userPlan !== 'pro'}
                    style={userPlan !== 'pro' ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                    title={userPlan !== 'pro' ? 'Playoffs require a Commissioner plan' : ''}
                  >
                    Playoffs{userPlan !== 'pro' ? ' \uD83D\uDD12' : ''}
                  </button>
                )}
                <button
                  onClick={() => handleDownloadPDF(league)}
                  className="action-button secondary"
                >
                  PDF
                </button>
                {!isCompleted && league.status !== 'draft' && league.isPublic && (
                  <button
                    onClick={() => setEmbedModal({ leagueName: league.name, slug: league.slug })}
                    className="action-button secondary"
                  >
                    Embed
                  </button>
                )}
                {!isCompleted && league.status !== 'draft' && userPlan !== 'free' && (
                  <button
                    onClick={() => handleNotifyCaptains(league.id)}
                    className="action-button secondary"
                    disabled={notifyingLeague === league.id}
                    style={notifyingLeague === league.id ? { opacity: 0.6, cursor: 'wait' } : {}}
                  >
                    {notifyingLeague === league.id ? 'Sending...' : 'Notify Captains'}
                  </button>
                )}
                {!isCompleted && league.status !== 'draft' && userPlan !== 'free' && (
                  <button
                    onClick={() => handleSendReminders(league.id)}
                    className="action-button secondary"
                    disabled={remindingLeague === league.id}
                    style={remindingLeague === league.id ? { opacity: 0.6, cursor: 'wait' } : {}}
                  >
                    {remindingLeague === league.id ? 'Sending...' : 'Send Reminders'}
                  </button>
                )}
                {!isCompleted && league.status !== 'draft' && userPlan !== 'free' && (
                  <button
                    onClick={() => handleGenerateCaptainLinks(league.id, league.name)}
                    className="action-button secondary"
                    disabled={generatingLinks === league.id}
                    style={generatingLinks === league.id ? { opacity: 0.6, cursor: 'wait' } : {}}
                  >
                    {generatingLinks === league.id ? 'Generating...' : 'Captain Links'}
                  </button>
                )}
                {!isCompleted && league.status !== 'draft' && userPlan === 'pro' && (
                  <button
                    onClick={() => {
                      setArchiveSeasonName('');
                      setArchiveModal({ leagueId: league.id, leagueName: league.name });
                    }}
                    className="action-button secondary"
                  >
                    Archive Season
                  </button>
                )}
                {userPlan === 'pro' && (
                  <button
                    onClick={() => handleOpenSeasons(league.id, league.name)}
                    className="action-button secondary"
                    disabled={loadingSeasons === league.id}
                    style={loadingSeasons === league.id ? { opacity: 0.6, cursor: 'wait' } : {}}
                  >
                    {loadingSeasons === league.id ? 'Loading...' : 'Past Seasons'}
                  </button>
                )}
                {!isCompleted && league.pendingScoreCount > 0 && userPlan === 'pro' && (
                  <button
                    onClick={() => handleOpenPendingScores(league.id, league.name)}
                    className="action-button secondary"
                    style={{ position: 'relative' }}
                  >
                    Review Scores
                    <span style={{
                      position: 'absolute',
                      top: -6,
                      right: -6,
                      background: '#e67e22',
                      color: '#fff',
                      fontSize: '0.7rem',
                      fontWeight: 700,
                      width: 20,
                      height: 20,
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>{league.pendingScoreCount}</span>
                  </button>
                )}
                {isCompleted && (
                  <button
                    onClick={() => handleReactivateLeague(league.id)}
                    className="action-button secondary"
                  >
                    Reactivate
                  </button>
                )}
                {deleteMode && (
                  <>
                    {!isCompleted && (
                      <button
                        onClick={() => handleDuplicateLeague(league)}
                        className="action-button secondary"
                        disabled={atLeagueLimit}
                        style={atLeagueLimit ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                        title={atLeagueLimit ? 'League limit reached. Upgrade to Commissioner for unlimited leagues.' : 'Duplicate this league'}
                      >
                        Duplicate
                      </button>
                    )}
                    {!isCompleted && league.status === 'published' && (
                      <button
                        onClick={() => setConfirmComplete({ leagueId: league.id, leagueName: league.name })}
                        className="action-button secondary"
                      >
                        Mark Completed
                      </button>
                    )}
                    <button
                      onClick={() => setConfirmDelete({ leagueId: league.id, leagueName: league.name })}
                      className="action-button danger"
                    >
                      Delete
                    </button>
                  </>
                )}
              </div>

            </div>
            );
          })}
        </div>
          )}
        </>
      )}

      {/* Mark completed confirmation modal */}
      {confirmComplete && (
        <div className="dash-modal-overlay" onClick={() => setConfirmComplete(null)}>
          <div className="dash-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Mark League as Completed</h3>
            <p>Mark <strong>"{confirmComplete.leagueName}"</strong> as completed?</p>
            <p style={{ fontSize: '0.85rem', color: colors.textSecondary, margin: '8px 0' }}>
              The league will move to the Completed tab. The public page will remain accessible.
              You can reactivate it at any time.
            </p>
            <div className="dash-modal-actions">
              <button className="dash-modal-btn dash-modal-cancel" onClick={() => setConfirmComplete(null)}>
                Cancel
              </button>
              <button
                className="dash-modal-btn"
                style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff' }}
                onClick={handleMarkComplete}
              >
                Mark Completed
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Branded delete confirmation modal */}
      {confirmDelete && (
        <div className="dash-modal-overlay" onClick={() => setConfirmDelete(null)}>
          <div className="dash-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Delete League</h3>
            <p>Are you sure you want to delete <strong>"{confirmDelete.leagueName}"</strong>?</p>
            <p className="dash-modal-warning">This will permanently remove the league, all teams, games, and scores. This cannot be undone.</p>
            <div className="dash-modal-actions">
              <button
                className="dash-modal-btn dash-modal-cancel"
                onClick={() => setConfirmDelete(null)}
              >
                Cancel
              </button>
              <button
                className="dash-modal-btn dash-modal-confirm"
                onClick={handleDelete}
              >
                Delete League
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pending scores review modal */}
      {pendingScoresModal && (
        <div className="dash-modal-overlay" onClick={() => setPendingScoresModal(null)}>
          <div className="dash-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 540 }}>
            <h3>Review Scores — {pendingScoresModal.leagueName}</h3>
            {pendingScoresModal.scores.length === 0 ? (
              <p style={{ color: colors.textSecondary, fontSize: '0.9rem', margin: '16px 0' }}>No pending scores to review.</p>
            ) : (
              <div style={{ maxHeight: 400, overflowY: 'auto', marginBottom: 12 }}>
                {pendingScoresModal.scores.map((ps) => (
                  <div key={ps.id} style={{
                    padding: '12px',
                    background: colors.tableStripe,
                    borderRadius: 8,
                    marginBottom: 8,
                    border: `1px solid ${colors.border}`
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                      <span style={{ fontSize: '0.82rem', color: colors.textSecondary }}>
                        Week {ps.game.week} &bull; {ps.game.date} &bull; {ps.game.time}
                      </span>
                      <span style={{
                        fontSize: '0.72rem',
                        color: ps.submitterType === 'referee' ? '#1565c0' : colors.brand,
                        background: ps.submitterType === 'referee' ? '#e3f2fd' : colors.badgeBg,
                        padding: '2px 8px',
                        borderRadius: 4
                      }}>
                        {ps.submitterType === 'referee'
                          ? `Referee: ${ps.submittedByReferee?.name || 'Unknown'}`
                          : `Captain: ${ps.submittedBy?.name || 'Unknown'}`}
                      </span>
                    </div>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 12,
                      margin: '8px 0',
                      fontSize: '1rem'
                    }}>
                      <span style={{ fontWeight: 600, color: colors.text }}>{ps.game.team1?.name || 'TBD'}</span>
                      <span style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: '#fff',
                        padding: '4px 12px',
                        borderRadius: 6,
                        fontWeight: 700,
                        fontSize: '1.1rem'
                      }}>
                        {ps.team1Score} - {ps.team2Score}
                      </span>
                      <span style={{ fontWeight: 600, color: colors.text }}>{ps.game.team2?.name || 'TBD'}</span>
                    </div>
                    <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                      <button
                        onClick={() => handleReviewScore(ps.id, 'approved')}
                        disabled={reviewingScore === ps.id}
                        style={{
                          flex: 1,
                          padding: '8px',
                          background: '#27ae60',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 6,
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          cursor: reviewingScore === ps.id ? 'wait' : 'pointer',
                          opacity: reviewingScore === ps.id ? 0.6 : 1
                        }}
                      >
                        {reviewingScore === ps.id ? '...' : 'Approve'}
                      </button>
                      <button
                        onClick={() => handleReviewScore(ps.id, 'rejected')}
                        disabled={reviewingScore === ps.id}
                        style={{
                          flex: 1,
                          padding: '8px',
                          background: colors.danger,
                          color: '#fff',
                          border: 'none',
                          borderRadius: 6,
                          fontSize: '0.85rem',
                          fontWeight: 600,
                          cursor: reviewingScore === ps.id ? 'wait' : 'pointer',
                          opacity: reviewingScore === ps.id ? 0.6 : 1
                        }}
                      >
                        {reviewingScore === ps.id ? '...' : 'Reject'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <div className="dash-modal-actions">
              <button
                className="dash-modal-btn dash-modal-cancel"
                onClick={() => setPendingScoresModal(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Captain links modal */}
      {captainLinks && (
        <div className="dash-modal-overlay" onClick={() => setCaptainLinks(null)}>
          <div className="dash-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <h3>Captain Links — {captainLinks.leagueName}</h3>
            <p style={{ fontSize: '0.85rem', color: colors.textSecondary, margin: '0 0 12px' }}>
              Share these links with team captains. Each link expires in 90 days.
            </p>
            <div style={{ maxHeight: 300, overflowY: 'auto', marginBottom: 12 }}>
              {captainLinks.tokens.map((t, i) => (
                <div key={i} style={{
                  padding: '10px 12px',
                  background: i % 2 === 0 ? colors.tableStripe : colors.card,
                  borderRadius: 6,
                  marginBottom: 4
                }}>
                  <div style={{ fontWeight: 600, fontSize: '0.9rem', color: colors.text }}>{t.teamName}</div>
                  <div style={{ fontSize: '0.78rem', color: colors.textSecondary, marginBottom: 4 }}>{t.email}</div>
                  <div style={{
                    fontSize: '0.75rem',
                    color: colors.brand,
                    wordBreak: 'break-all',
                    cursor: 'pointer'
                  }}
                    onClick={() => {
                      navigator.clipboard.writeText(t.url);
                      showToast('Link copied!', 'success');
                    }}
                    title="Click to copy"
                  >
                    {t.url}
                  </div>
                </div>
              ))}
            </div>
            <div className="dash-modal-actions">
              <button
                className="dash-modal-btn dash-modal-cancel"
                onClick={() => setCaptainLinks(null)}
              >
                Close
              </button>
              <button
                className="dash-modal-btn"
                style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff' }}
                onClick={copyAllCaptainLinks}
              >
                Copy All Links
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Archive season confirmation modal */}
      {archiveModal && (
        <div className="dash-modal-overlay" onClick={() => setArchiveModal(null)}>
          <div className="dash-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Archive Season</h3>
            <p>Archive the current season for <strong>"{archiveModal.leagueName}"</strong>?</p>
            <p className="dash-modal-warning">
              This will save the current schedule and standings, then reset the league for a new season. This cannot be undone.
            </p>
            <div style={{ margin: '16px 0' }}>
              <label style={{ display: 'block', fontSize: '0.85rem', fontWeight: 600, color: colors.text, marginBottom: 6 }}>
                Season Name
              </label>
              <input
                type="text"
                value={archiveSeasonName}
                onChange={(e) => setArchiveSeasonName(e.target.value)}
                placeholder="e.g., Spring 2026"
                style={{
                  width: '100%',
                  padding: '0.6rem',
                  borderRadius: 6,
                  border: `1px solid ${colors.inputBorder}`,
                  fontSize: '1rem',
                  boxSizing: 'border-box'
                }}
                autoFocus
              />
            </div>
            <div className="dash-modal-actions">
              <button
                className="dash-modal-btn dash-modal-cancel"
                onClick={() => setArchiveModal(null)}
              >
                Cancel
              </button>
              <button
                className="dash-modal-btn"
                style={{
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  opacity: archivingLeague || !archiveSeasonName.trim() ? 0.6 : 1,
                  cursor: archivingLeague || !archiveSeasonName.trim() ? 'not-allowed' : 'pointer'
                }}
                onClick={handleArchiveSeason}
                disabled={archivingLeague || !archiveSeasonName.trim()}
              >
                {archivingLeague ? 'Archiving...' : 'Archive Season'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Past seasons list modal */}
      {seasonsModal && !seasonViewer && (
        <div className="dash-modal-overlay" onClick={() => setSeasonsModal(null)}>
          <div className="dash-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <h3>Past Seasons — {seasonsModal.leagueName}</h3>
            {seasonsModal.seasons.length === 0 ? (
              <p style={{ color: colors.textSecondary, fontSize: '0.9rem', margin: '16px 0' }}>No archived seasons yet.</p>
            ) : (
              <div style={{ maxHeight: 400, overflowY: 'auto', marginBottom: 12 }}>
                {seasonsModal.seasons.map((s) => (
                  <div
                    key={s.id}
                    onClick={() => handleViewSeason(seasonsModal.leagueId, s.id)}
                    style={{
                      padding: '12px 16px',
                      background: colors.tableStripe,
                      borderRadius: 8,
                      marginBottom: 8,
                      border: `1px solid ${colors.border}`,
                      cursor: 'pointer',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      transition: 'background 0.15s'
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = colors.cardHover}
                    onMouseLeave={(e) => e.currentTarget.style.background = colors.tableStripe}
                  >
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.95rem', color: colors.text }}>{s.name}</div>
                      <div style={{ fontSize: '0.78rem', color: colors.textSecondary, marginTop: 2 }}>
                        Archived {formatDate(s.archivedAt)}
                      </div>
                    </div>
                    <span style={{ color: colors.brand, fontSize: '0.85rem', fontWeight: 500 }}>View →</span>
                  </div>
                ))}
              </div>
            )}
            <div className="dash-modal-actions">
              <button
                className="dash-modal-btn dash-modal-cancel"
                onClick={() => setSeasonsModal(null)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}


      {/* Season detail viewer modal */}
      {seasonViewer && (
        <div className="dash-modal-overlay" onClick={() => setSeasonViewer(null)}>
          <div className="dash-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640, maxHeight: '80vh', display: 'flex', flexDirection: 'column' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h3 style={{ margin: 0 }}>{seasonViewer.season.name}</h3>
              <span style={{ fontSize: '0.78rem', color: colors.textSecondary }}>
                Archived {formatDate(seasonViewer.season.archivedAt)}
              </span>
            </div>

            {loadingSeasonDetail ? (
              <p style={{ color: colors.textSecondary, textAlign: 'center', margin: '2rem 0' }}>Loading season data...</p>
            ) : (
              <div style={{ overflowY: 'auto', flex: 1 }}>
                {/* Standings */}
                {seasonViewer.season.standings && seasonViewer.season.standings.length > 0 && (
                  <div style={{ marginBottom: 20 }}>
                    <h4 style={{ fontSize: '0.95rem', color: colors.brand, margin: '0 0 8px', fontWeight: 600 }}>Standings</h4>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                        <thead>
                          <tr style={{ borderBottom: `2px solid ${colors.brand}`, textAlign: 'left' }}>
                            <th style={{ padding: '6px 8px', color: colors.text }}>#</th>
                            <th style={{ padding: '6px 8px', color: colors.text }}>Team</th>
                            <th style={{ padding: '6px 8px', color: colors.text, textAlign: 'center' }}>W</th>
                            <th style={{ padding: '6px 8px', color: colors.text, textAlign: 'center' }}>L</th>
                            <th style={{ padding: '6px 8px', color: colors.text, textAlign: 'center' }}>T</th>
                            <th style={{ padding: '6px 8px', color: colors.text, textAlign: 'center' }}>PTS</th>
                            <th style={{ padding: '6px 8px', color: colors.text, textAlign: 'center' }}>GF</th>
                            <th style={{ padding: '6px 8px', color: colors.text, textAlign: 'center' }}>GA</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...seasonViewer.season.standings]
                            .sort((a, b) => {
                              if (b.points !== a.points) return b.points - a.points;
                              const aDiff = a.goalsFor - a.goalsAgainst;
                              const bDiff = b.goalsFor - b.goalsAgainst;
                              if (bDiff !== aDiff) return bDiff - aDiff;
                              return b.goalsFor - a.goalsFor;
                            })
                            .map((team, i) => (
                              <tr key={i} style={{ borderBottom: `1px solid ${colors.border}`, background: i % 2 === 0 ? colors.tableStripe : colors.card }}>
                                <td style={{ padding: '6px 8px', color: colors.textSecondary }}>{i + 1}</td>
                                <td style={{ padding: '6px 8px', fontWeight: 600, color: colors.text }}>{team.teamName}</td>
                                <td style={{ padding: '6px 8px', textAlign: 'center' }}>{team.wins}</td>
                                <td style={{ padding: '6px 8px', textAlign: 'center' }}>{team.losses}</td>
                                <td style={{ padding: '6px 8px', textAlign: 'center' }}>{team.ties}</td>
                                <td style={{ padding: '6px 8px', textAlign: 'center', fontWeight: 700, color: colors.brand }}>{team.points}</td>
                                <td style={{ padding: '6px 8px', textAlign: 'center' }}>{team.goalsFor}</td>
                                <td style={{ padding: '6px 8px', textAlign: 'center' }}>{team.goalsAgainst}</td>
                              </tr>
                            ))
                          }
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {/* Playoff Results */}
                {seasonViewer.season.playoffResults && (
                  <div style={{ marginBottom: 20 }}>
                    <h4 style={{ fontSize: '0.95rem', color: colors.brand, margin: '0 0 8px', fontWeight: 600 }}>Playoff Results</h4>
                    {seasonViewer.season.playoffResults.rounds.map((round) => (
                      <div key={round.round} style={{ marginBottom: 12 }}>
                        <div style={{
                          fontSize: '0.82rem',
                          fontWeight: 600,
                          color: colors.brand,
                          padding: '4px 0',
                          marginBottom: 4,
                          borderBottom: `1px solid ${colors.border}`
                        }}>
                          {round.name}
                        </div>
                        {round.games.map((game, gi) => (
                          <div key={gi} style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 10,
                            padding: '6px 0',
                            fontSize: '0.88rem'
                          }}>
                            <span style={{ fontWeight: 600, color: colors.text, textAlign: 'right', flex: 1 }}>
                              {game.team1?.seed && <span style={{ color: colors.brand, fontSize: '0.75rem', marginRight: 4 }}>({game.team1.seed})</span>}
                              {game.team1?.name || 'TBD'}
                            </span>
                            {game.played ? (
                              <span style={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                color: '#fff',
                                padding: '2px 10px',
                                borderRadius: 6,
                                fontWeight: 700,
                                fontSize: '0.85rem',
                                minWidth: 50,
                                textAlign: 'center'
                              }}>
                                {game.team1Score != null ? `${game.team1Score} - ${game.team2Score}` : 'Override'}
                                {game.overrideWinnerSlot && (
                                  <span title={`Admin Override${game.overrideReason ? ': ' + game.overrideReason : ''}`}
                                        style={{ cursor: 'help', marginLeft: 4, color: '#ffc107' }}>*</span>
                                )}
                              </span>
                            ) : (
                              <span style={{ color: colors.textSecondary, fontSize: '0.82rem' }}>vs</span>
                            )}
                            <span style={{ fontWeight: 600, color: colors.text, textAlign: 'left', flex: 1 }}>
                              {game.team2?.name || 'TBD'}
                              {game.team2?.seed && <span style={{ color: colors.brand, fontSize: '0.75rem', marginLeft: 4 }}>({game.team2.seed})</span>}
                            </span>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}

                {/* Schedule */}
                {seasonViewer.season.scheduleData && (
                  <div>
                    <h4 style={{ fontSize: '0.95rem', color: colors.brand, margin: '0 0 8px', fontWeight: 600 }}>Schedule</h4>
                    {(() => {
                      const sd = seasonViewer.season.scheduleData;
                      const weeks = sd.schedule || sd.weeks || [];
                      return weeks.map((week, wi) => (
                        <div key={wi} style={{ marginBottom: 12 }}>
                          <div style={{
                            fontSize: '0.82rem',
                            fontWeight: 600,
                            color: colors.brand,
                            padding: '4px 0',
                            marginBottom: 4,
                            borderBottom: `1px solid ${colors.border}`,
                            display: 'flex',
                            justifyContent: 'space-between'
                          }}>
                            <span>Week {week.week || wi + 1}</span>
                            <span style={{ color: colors.textSecondary, fontWeight: 400 }}>{week.date ? formatDate(week.date) : ''}</span>
                          </div>
                          {(week.games || []).map((game, gi) => (
                            <div key={gi} style={{
                              display: 'flex',
                              alignItems: 'center',
                              padding: '4px 8px',
                              fontSize: '0.85rem',
                              background: gi % 2 === 0 ? colors.tableStripe : colors.card,
                              borderRadius: 4
                            }}>
                              <span style={{ color: colors.textSecondary, fontSize: '0.78rem', width: 70, flexShrink: 0 }}>{game.time}</span>
                              <span style={{ flex: 1, textAlign: 'center' }}>
                                <span style={{ fontWeight: 500 }}>{game.team1 || game.team1Name}</span>
                                <span style={{ color: colors.textSecondary, margin: '0 8px' }}>vs</span>
                                <span style={{ fontWeight: 500 }}>{game.team2 || game.team2Name}</span>
                              </span>
                            </div>
                          ))}
                        </div>
                      ));
                    })()}
                  </div>
                )}
              </div>
            )}

            <div className="dash-modal-actions" style={{ marginTop: 12 }}>
              <button
                className="dash-modal-btn dash-modal-cancel"
                onClick={() => setSeasonViewer(null)}
              >
                {seasonsModal ? '← Back' : 'Close'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customize Public Page Modal */}
      {customizeModal && (
        <div className="dash-modal-overlay" onClick={() => setCustomizeModal(null)}>
          <div className="dash-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
            <h3 style={{ margin: '0 0 8px', color: colors.text }}>Customize Public Page &mdash; {customizeModal.leagueName}</h3>
            <p style={{ margin: '0 0 16px', fontSize: '0.85rem', color: colors.textSecondary }}>
              Personalize how your league appears on its public page.
            </p>

            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: colors.textSecondary, marginBottom: 4 }}>
              Public URL Slug
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: slugValidation.message ? 2 : 12 }}>
              <span style={{
                padding: '8px 8px 8px 10px',
                background: colors.bgSecondary || colors.inputBg,
                border: `1px solid ${slugValidation.status === 'error' ? '#e53e3e' : slugValidation.status === 'valid' ? '#38a169' : colors.inputBorder}`,
                borderRight: 'none',
                borderRadius: '6px 0 0 6px',
                fontSize: '0.8rem',
                color: colors.textSecondary,
                whiteSpace: 'nowrap',
                boxSizing: 'border-box'
              }}>
                /public/
              </span>
              <div style={{ position: 'relative', flex: 1 }}>
                <input
                  type="text"
                  value={customizeForm.slug}
                  onChange={(e) => handleSlugChange(e.target.value)}
                  placeholder="my-league-2025"
                  style={{
                    width: '100%',
                    padding: '8px 30px 8px 10px',
                    border: `1px solid ${slugValidation.status === 'error' ? '#e53e3e' : slugValidation.status === 'valid' ? '#38a169' : colors.inputBorder}`,
                    borderRadius: '0 6px 6px 0',
                    fontSize: '0.85rem',
                    background: colors.inputBg,
                    color: colors.text,
                    boxSizing: 'border-box'
                  }}
                />
                {slugValidation.status === 'valid' && (
                  <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: '#38a169', fontSize: '1rem' }}>&#10003;</span>
                )}
                {slugValidation.status === 'checking' && (
                  <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: colors.textSecondary, fontSize: '0.75rem' }}>...</span>
                )}
              </div>
            </div>
            {slugValidation.message && (
              <p style={{
                margin: '0 0 12px',
                fontSize: '0.75rem',
                color: slugValidation.status === 'error' ? '#e53e3e' : slugValidation.status === 'valid' ? '#38a169' : colors.textSecondary
              }}>
                {slugValidation.message}
              </p>
            )}
            <div style={{ marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: '0.8rem', color: colors.textSecondary, wordBreak: 'break-all' }}>
                {getPublicUrl(customizeForm.slug || customizeModal.slug)}
              </span>
              <a
                href={getPublicUrl(customizeForm.slug || customizeModal.slug)}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontSize: '0.75rem',
                  color: colors.brand || '#667eea',
                  textDecoration: 'none',
                  fontWeight: 600,
                  whiteSpace: 'nowrap'
                }}
              >
                Open ↗
              </a>
            </div>

            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: colors.textSecondary, marginBottom: 4 }}>
              Logo URL
            </label>
            <div style={{ position: 'relative', marginBottom: logoUrlValidation.message ? 2 : 12 }}>
              <input
                type="url"
                value={customizeForm.logoUrl}
                onChange={(e) => handleLogoUrlChange(e.target.value)}
                placeholder="https://example.com/logo.png"
                style={{
                  width: '100%',
                  padding: '8px 30px 8px 10px',
                  border: `1px solid ${logoUrlValidation.status === 'error' ? '#e53e3e' : logoUrlValidation.status === 'valid' ? '#38a169' : colors.inputBorder}`,
                  borderRadius: 6,
                  fontSize: '0.85rem',
                  background: colors.inputBg,
                  color: colors.text,
                  boxSizing: 'border-box'
                }}
              />
              {logoUrlValidation.status === 'valid' && (
                <span style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: '#38a169', fontSize: '1rem' }}>&#10003;</span>
              )}
            </div>
            {logoUrlValidation.message && (
              <p style={{
                margin: '0 0 12px',
                fontSize: '0.75rem',
                color: '#e53e3e'
              }}>
                {logoUrlValidation.message}
              </p>
            )}

            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: colors.textSecondary, marginBottom: 4 }}>
              Description <span style={{ fontWeight: 400, color: colors.textSecondary }}>({(customizeForm.description || '').length}/500)</span>
            </label>
            <textarea
              value={customizeForm.description}
              onChange={(e) => {
                if (e.target.value.length <= 500) {
                  setCustomizeForm(f => ({ ...f, description: e.target.value }));
                }
              }}
              placeholder="A short description of your league..."
              rows={3}
              style={{
                width: '100%',
                padding: '8px 10px',
                border: `1px solid ${colors.inputBorder}`,
                borderRadius: 6,
                fontSize: '0.85rem',
                background: colors.inputBg,
                color: colors.text,
                marginBottom: 12,
                boxSizing: 'border-box',
                resize: 'vertical',
                fontFamily: 'inherit'
              }}
            />

            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: colors.textSecondary, marginBottom: 4 }}>
                  City
                </label>
                <input
                  type="text"
                  value={customizeForm.city}
                  onChange={(e) => setCustomizeForm(f => ({ ...f, city: e.target.value }))}
                  placeholder="e.g., San Diego"
                  style={{
                    width: '100%', padding: '8px 10px', border: `1px solid ${colors.inputBorder}`,
                    borderRadius: 6, fontSize: '0.85rem', background: colors.inputBg, color: colors.text, boxSizing: 'border-box'
                  }}
                />
              </div>
              <div style={{ flex: 1 }}>
                <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: colors.textSecondary, marginBottom: 4 }}>
                  State
                </label>
                <input
                  type="text"
                  value={customizeForm.state}
                  onChange={(e) => setCustomizeForm(f => ({ ...f, state: e.target.value }))}
                  placeholder="e.g., CA"
                  style={{
                    width: '100%', padding: '8px 10px', border: `1px solid ${colors.inputBorder}`,
                    borderRadius: 6, fontSize: '0.85rem', background: colors.inputBg, color: colors.text, boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: colors.textSecondary, marginBottom: 4 }}>
              Primary Color
            </label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 16 }}>
              <input
                type="color"
                value={customizeForm.primaryColor || '#667eea'}
                onChange={(e) => setCustomizeForm(f => ({ ...f, primaryColor: e.target.value }))}
                style={{
                  width: 40,
                  height: 36,
                  border: `1px solid ${colors.inputBorder}`,
                  borderRadius: 6,
                  padding: 2,
                  cursor: 'pointer',
                  background: colors.inputBg
                }}
              />
              <input
                type="text"
                value={customizeForm.primaryColor}
                onChange={(e) => setCustomizeForm(f => ({ ...f, primaryColor: e.target.value }))}
                placeholder="#667eea"
                style={{
                  flex: 1,
                  padding: '8px 10px',
                  border: `1px solid ${colors.inputBorder}`,
                  borderRadius: 6,
                  fontSize: '0.85rem',
                  background: colors.inputBg,
                  color: colors.text,
                  fontFamily: 'monospace'
                }}
              />
              {customizeForm.primaryColor && (
                <button
                  onClick={() => setCustomizeForm(f => ({ ...f, primaryColor: '' }))}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '0.8rem',
                    color: colors.textSecondary,
                    padding: '4px 8px'
                  }}
                >
                  Reset
                </button>
              )}
            </div>

            {/* Preview */}
            <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: colors.textSecondary, marginBottom: 4 }}>
              Preview
            </label>
            <div style={{
              background: customizeForm.primaryColor
                ? `linear-gradient(135deg, ${customizeForm.primaryColor} 0%, ${customizeForm.primaryColor}cc 100%)`
                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              borderRadius: 8,
              padding: '16px 20px',
              textAlign: 'center',
              marginBottom: 16
            }}>
              {customizeForm.logoUrl && logoUrlValidation.status !== 'error' && (
                <img
                  src={customizeForm.logoUrl}
                  alt="League logo"
                  style={{ maxHeight: 60, maxWidth: 200, marginBottom: 8, display: 'block', marginLeft: 'auto', marginRight: 'auto' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              )}
              <div style={{ color: '#fff', fontWeight: 700, fontSize: '1.1rem' }}>
                {customizeModal.leagueName}
              </div>
              {customizeForm.description && (
                <div style={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.8rem', marginTop: 4 }}>
                  {customizeForm.description.length > 100 ? customizeForm.description.slice(0, 100) + '...' : customizeForm.description}
                </div>
              )}
            </div>

            <div className="dash-modal-actions" style={{ marginTop: 0 }}>
              <button
                className="dash-modal-btn dash-modal-cancel"
                onClick={() => setCustomizeModal(null)}
              >
                Cancel
              </button>
              <button
                className="dash-modal-btn"
                onClick={handleSaveCustomize}
                disabled={savingCustomize || hasCustomizeErrors}
                style={{
                  background: (savingCustomize || hasCustomizeErrors) ? colors.border : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: '#fff',
                  cursor: (savingCustomize || hasCustomizeErrors) ? 'not-allowed' : 'pointer'
                }}
              >
                {savingCustomize ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Embed Code Modal */}
      {embedModal && (() => {
        const embedUrl = `${window.location.origin}/embed/${embedModal.slug}`;
        const iframeCode = `<iframe src="${embedUrl}" width="100%" height="600" frameborder="0" style="border: none;"></iframe>`;
        const autoResizeSnippet = `<script>
window.addEventListener('message', function(e) {
  if (e.data && e.data.type === 'leaguemaker-height') {
    var frames = document.querySelectorAll('iframe[src*="/embed/"]');
    frames.forEach(function(f) { f.style.height = e.data.height + 'px'; });
  }
});
</script>`;
        const fullCode = iframeCode + '\n' + autoResizeSnippet;
        return (
          <div className="dash-modal-overlay" onClick={() => setEmbedModal(null)}>
            <div className="dash-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
              <h3 style={{ margin: '0 0 8px', color: colors.text }}>Embed League &mdash; {embedModal.leagueName}</h3>
              <p style={{ margin: '0 0 16px', fontSize: '0.85rem', color: colors.textSecondary }}>
                Copy this code and paste it into your website's HTML to embed your league schedule.
              </p>

              <label style={{ display: 'block', fontSize: '0.8rem', fontWeight: 600, color: colors.textSecondary, marginBottom: 4 }}>
                Embed Code
              </label>
              <pre style={{
                background: colors.bgSecondary,
                border: `1px solid ${colors.border}`,
                borderRadius: 6,
                padding: 12,
                fontSize: '0.78rem',
                lineHeight: 1.5,
                overflowX: 'auto',
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-all',
                color: colors.text,
                margin: '0 0 16px',
              }}>
                {fullCode}
              </pre>

              <div className="dash-modal-actions" style={{ marginTop: 0 }}>
                <button
                  className="dash-modal-btn dash-modal-cancel"
                  onClick={() => setEmbedModal(null)}
                >
                  Close
                </button>
                <button
                  className="dash-modal-btn"
                  style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: '#fff' }}
                  onClick={() => {
                    navigator.clipboard.writeText(fullCode);
                    showToast('Embed code copied!', 'success');
                  }}
                >
                  Copy Code
                </button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

export default Dashboard;
